import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  ShoppingBag, 
  ArrowRight, 
  Trash2, 
  Star, 
  Minus, 
  Plus, 
  Shield, 
  CreditCard, 
  Clock, 
  AlertCircle,
  CheckCircle,
  Calendar
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useCart, type CartItem } from '../contexts/CartContext';

interface CartTotals {
  subtotal: number;
  savings: number;
  total: number;
}

export default function CartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { cartItems, updateQuantity: updateCartQuantity, removeFromCart } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { returnPath: '/cart' } });
    }
  }, [user, navigate]);

  // Show loading while checking authentication
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const calculateTotals = (items: CartItem[]): CartTotals => {
    const subtotal = items.reduce((sum: number, item: CartItem) => {
      let itemTotal = item.price * item.quantity;
      if (item.addons) {
        itemTotal += item.addons.reduce((sum: number, addon) => 
          sum + addon.price * item.quantity, 0);
      }
      return sum + itemTotal;
    }, 0);

    const savings = items.reduce((sum: number, item: CartItem) => 
      sum + ((item.originalPrice || item.price) - item.price) * item.quantity, 0);

    return {
      subtotal,
      savings,
      total: subtotal
    };
  };

  const { subtotal, savings, total } = calculateTotals(cartItems);

  const calculateItemTotal = (item: CartItem): number => {
    let itemTotal = item.price * item.quantity;
    if (item.addons) {
      itemTotal += item.addons.reduce((sum: number, addon) => 
        sum + addon.price * item.quantity, 0);
    }
    return itemTotal;
  };

  const updateQuantity = (id: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(id);
      return;
    }
    updateCartQuantity(id, newQuantity);
    setToastMessage('Cart updated successfully');
    setToastType('success');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const removeItem = (id: string) => {
    removeFromCart(id);
    setToastMessage('Item removed from cart');
    setToastType('success');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const handleCheckout = () => {
    if (!user) {
      setToastMessage('Please login to checkout');
      setToastType('error');
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        navigate('/login', { state: { returnPath: location.pathname } });
      }, 2000);
      return;
    }
    navigate('/checkout');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[600px] p-6">
        <div className="flex items-center text-red-500 mb-4">
          <AlertCircle className="h-6 w-6 mr-2" />
          <span>Error loading cart</span>
        </div>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  // Empty cart state
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Looks like you haven't added any services yet.</p>
          <Link
            to="/services"
            className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors inline-flex items-center"
          >
            Browse Services
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Toast Notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed top-4 right-4 z-50"
          >
            <div className={`flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
              toastType === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {toastType === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="font-medium">{toastMessage}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
            <p className="text-gray-600 mt-2">
              {cartItems.length} service{cartItems.length !== 1 ? 's' : ''} in your cart
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-8">
              <div className="space-y-6">
                {cartItems.map((item: CartItem, index: number) => (
                  <motion.div
                    key={item.cartItemId || item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-md p-4 md:p-6"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-48 sm:w-24 sm:h-24 rounded-lg object-cover flex-shrink-0"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
                          {item.name}
                        </h3>
                        
                        <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-600 mb-2">
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                            <span>{item.duration}</span>
                          </div>
                          
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                            <span>{item.date}</span>
                          </div>
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                            <span>{item.time}</span>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 md:gap-4 mb-3">
                          <span className="text-lg md:text-xl font-bold text-gray-900">
                            ₹{item.price * item.quantity}
                          </span>
                          {item.originalPrice && (
                            <>
                              <span className="text-xs md:text-sm text-gray-500 line-through">
                                ₹{item.originalPrice * item.quantity}
                              </span>
                              <span className="text-xs md:text-sm text-green-600 font-medium">
                                Save ₹{(item.originalPrice - item.price) * item.quantity}
                              </span>
                            </>
                          )}
                        </div>

                        {/* Addons */}
                        {item.addons && item.addons.length > 0 && (
                          <div className="mt-2 space-y-1 p-2 bg-gray-50 rounded-lg">
                            <p className="text-xs md:text-sm font-medium text-gray-700">Add-ons:</p>
                            {item.addons.map((addon) => (
                              <div 
                                key={addon.id} 
                                className="flex justify-between text-xs md:text-sm text-gray-600"
                              >
                                <span>• {addon.name}</span>
                                <span className="font-medium">₹{addon.price * item.quantity}</span>
                              </div>
                            ))}
                            <div className="flex justify-between text-sm md:text-base font-bold text-gray-900 pt-2 border-t border-gray-200 mt-2">
                              <span>Total:</span>
                              <span>₹{calculateItemTotal(item)}</span>
                            </div>
                          </div>
                        )}

                        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center justify-between sm:justify-start gap-3">
                            <div className="flex items-center bg-gray-100 rounded-lg">
                              <button
                                onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity - 1)}
                                className="p-2 hover:bg-gray-200 rounded-l-lg transition-colors"
                                aria-label="Decrease quantity"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <span className="px-4 py-2 font-medium min-w-[3rem] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity + 1)}
                                className="p-2 hover:bg-gray-200 rounded-r-lg transition-colors"
                                aria-label="Increase quantity"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                            
                            <button
                              onClick={() => removeItem(item.cartItemId || item.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              aria-label="Remove item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="flex items-center gap-2 bg-primary-50 px-3 py-2 rounded-lg">
                            <Clock className="h-4 w-4 text-primary-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-xs text-gray-600">Appointment</div>
                              <div className="font-medium text-xs md:text-sm text-gray-900 truncate">
                                {item.date} at {item.time}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Continue Shopping */}
              <div className="mt-8">
                <Link
                  to="/services"
                  className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center"
                >
                  <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                  Continue Shopping
                </Link>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-4 mt-8 lg:mt-0">
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">
                  Order Summary
                </h2>

                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-900">₹{subtotal}</span>
                  </div>

                  {savings > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600">Total Savings</span>
                      <span className="font-medium text-green-600">-₹{savings}</span>
                    </div>
                  )}

                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex justify-between">
                      <span className="text-base font-semibold text-gray-900">
                        Total
                      </span>
                      <span className="text-xl font-bold text-gray-900">
                        ₹{total}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full mt-6 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold 
                           hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 
                           focus:ring-offset-2 transition-colors"
                >
                  Proceed to Checkout
                </button>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Shield className="h-5 w-5 text-gray-400 mr-2" />
                    100% Secure Payments
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <CreditCard className="h-5 w-5 text-gray-400 mr-2" />
                    All Major Cards Accepted
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}